import { useEffect, useRef, useState, type FocusEvent } from 'react'
import { useReducedMotion } from './useReducedMotion'

/**
 * Pins the section and turns vertical scroll into horizontal travel, the way
 * product pages walk through features. Narrow screens and reduced motion get a
 * native horizontal scroller instead.
 */
export function usePinnedTrack(count: number) {
  const section = useRef<HTMLElement>(null)
  const track = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()
  const [pinned, setPinned] = useState(false)

  useEffect(() => {
    const query = window.matchMedia('(min-width: 62rem)')
    const update = () => setPinned(query.matches && !reduced)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [reduced])

  useEffect(() => {
    const el = section.current
    const rail = track.current
    if (!pinned || !el || !rail) {
      if (rail) rail.style.transform = ''
      return
    }
    let frame = 0
    const render = () => {
      frame = 0
      const rect = el.getBoundingClientRect()
      const travel = el.offsetHeight - window.innerHeight
      const progress = Math.min(1, Math.max(0, -rect.top / Math.max(1, travel)))
      const distance = Math.max(0, rail.scrollWidth - (rail.parentElement?.clientWidth ?? window.innerWidth))
      rail.style.transform = `translate3d(${-progress * distance}px, 0, 0)`
      el.style.setProperty('--tour-progress', progress.toFixed(4))
    }
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(render)
    }
    // A sideways swipe moves through the panels too, faster than a vertical
    // one, instead of the browser's back and forward gesture. That gesture
    // is a horizontal overscroll of the page, so the page turns it off only
    // while the pointer is over the section.
    const root = document.documentElement
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return
      event.preventDefault()
      window.scrollBy({ top: event.deltaX * 3, behavior: 'instant' })
    }
    const hold = () => root.classList.add('is-panning')
    const release = () => root.classList.remove('is-panning')
    render()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    el.addEventListener('wheel', onWheel, { passive: false })
    el.addEventListener('pointerenter', hold)
    el.addEventListener('pointerleave', release)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      el.removeEventListener('wheel', onWheel)
      el.removeEventListener('pointerenter', hold)
      el.removeEventListener('pointerleave', release)
      release()
      cancelAnimationFrame(frame)
    }
  }, [pinned, count])

  // Keyboard users tab into panels that sit off to the side: scroll the page
  // to where that panel is in view.
  const onFocus = (event: FocusEvent) => {
    const el = section.current
    const rail = track.current
    if (!pinned || !el || !rail) return
    const panel = (event.target as HTMLElement).closest<HTMLElement>('[data-panel]')
    if (!panel) return
    const index = Number(panel.dataset.panel)
    const travel = el.offsetHeight - window.innerHeight
    const top = el.getBoundingClientRect().top + window.scrollY
    window.scrollTo({ top: top + (travel * index) / Math.max(1, count - 1), behavior: 'instant' })
  }

  return { section, track, pinned, onFocus }
}
